/**
 * Constantes y helpers compartidos para las políticas de vacaciones.
 */

export const ACCRUAL_UNITS = [
  { value: "days", label: "Días" },
  { value: "hours", label: "Horas" },
];

export const ACCRUAL_MODES = [
  { value: "lump_sum", label: "Todo a la vez · primer día del ciclo" },
  { value: "monthly", label: "Mensualmente · proporcional" },
  { value: "daily", label: "Diariamente · proporcional" },
];

export const DISPLAY_UNITS = [
  { value: "whole", label: "Días enteros" },
  { value: "half", label: "Días y medios días" },
  { value: "quarter", label: "Cuartos de día" },
];

export const DAY_COUNT_BASES = [
  { value: "working_days", label: "Días laborables" },
  { value: "calendar_days", label: "Días naturales" },
];

export const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

export function defaultPolicy() {
  return {
    name: "",
    accrual_type: "days",
    allowance_per_year: 22,
    cycle_start_month: 1,
    cycle_duration_months: 12,
    display_unit: "whole",
    day_count_basis: "working_days",
    unlimited: false,
    holiday_inside_absence_counts: false,
    prorate_first_year: true,
    max_balance: null,
    accrual_mode: "lump_sum",
    allow_usage_in_next_cycle: false,
    allow_negative_balance: false,
    carryover_amount: 0,
    carryover_unlimited: false,
    carryover_expiration_months: 12,
    min_notice_days: 0,
    tenure_periods: [],
  };
}

export function summarizePolicy(p) {
  if (!p) return "";
  const unit = p.accrual_type === "hours" ? "h" : "d";
  const basis = p.day_count_basis === "calendar_days" ? "naturales" : "laborables";
  const amount = p.unlimited
    ? "Ilimitado"
    : `${Number(p.allowance_per_year || 0)}${unit} ${basis}`;
  return amount;
}

const DAY_MS = 86400000;
const YEAR_MS = 365.25 * DAY_MS;

const parseYmd = (str) => {
  if (!str) return null;
  const d = new Date(`${str}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const cycleRangeFor = (policy, today) => {
  const startMonth = Number(policy.cycle_start_month) || 1;
  const cycleMonths = Number(policy.cycle_duration_months) || 12;
  const refYear = today.getFullYear();
  const refMonth = today.getMonth() + 1;

  let cycleStart = new Date(refYear, startMonth - 1, 1);
  if (refMonth < startMonth) {
    cycleStart = new Date(refYear - 1, startMonth - 1, 1);
  }
  const cycleEnd = new Date(cycleStart);
  cycleEnd.setMonth(cycleEnd.getMonth() + cycleMonths);
  return { cycleStart, cycleEnd };
};

/**
 * Devuelve los días/horas acumulados según la política a fecha de hoy.
 * No descuenta usos (todavía no hay módulo de Solicitudes/Ausencias).
 *
 * Devuelve `null` si no hay política, `Infinity` si la política es ilimitada.
 */
/**
 * Derecho ANUAL del ciclo: base + tramo de antigüedad aplicable, SIN prorrateo
 * ni devengo. Es el "de N" frente al que se compara el saldo prorrateado.
 *
 * Devuelve `null` si no hay política, `Infinity` si es ilimitada.
 */
export function computeAnnualAllowance(policy, contractStartYmd, today = new Date()) {
  if (!policy) return null;
  if (policy.unlimited) return Infinity;

  let allowance = Number(policy.allowance_per_year) || 0;
  const contractStart = parseYmd(contractStartYmd);
  if (contractStart) {
    const yearsOfService = (today - contractStart) / YEAR_MS;
    const sorted = [...(policy.tenure_periods || [])].sort(
      (a, b) => Number(b.years_of_service) - Number(a.years_of_service),
    );
    for (const p of sorted) {
      if (yearsOfService >= Number(p.years_of_service)) {
        allowance += Number(p.extra_amount) || 0;
        break;
      }
    }
  }
  return allowance;
}

export function computeAccruedAllowance(policy, contractStartYmd, today = new Date()) {
  if (!policy) return null;
  if (policy.unlimited) return Infinity;

  const contractStart = parseYmd(contractStartYmd);
  const { cycleStart, cycleEnd } = cycleRangeFor(policy, today);

  // base + tramo de antigüedad (derecho anual, sin prorratear todavía).
  let allowance = computeAnnualAllowance(policy, contractStartYmd, today);

  const totalCycleDays = (cycleEnd - cycleStart) / DAY_MS;

  // Prorrateo si el contrato arranca dentro del ciclo actual.
  if (
    policy.prorate_first_year
    && contractStart
    && contractStart > cycleStart
    && contractStart < cycleEnd
  ) {
    const contractDaysInCycle = (cycleEnd - contractStart) / DAY_MS;
    const fraction = Math.max(0, Math.min(1, contractDaysInCycle / totalCycleDays));
    allowance = allowance * fraction;
  }

  // Devengo según modo
  const elapsedDays = Math.max(0, Math.min(totalCycleDays, (today - cycleStart) / DAY_MS));
  let accrued;
  switch (policy.accrual_mode) {
    case "monthly": {
      const cycleMonths = Number(policy.cycle_duration_months) || 12;
      const monthsElapsed = Math.min(cycleMonths, Math.floor((elapsedDays / totalCycleDays) * cycleMonths));
      accrued = (allowance / cycleMonths) * monthsElapsed;
      break;
    }
    case "daily":
      accrued = totalCycleDays > 0 ? (allowance * elapsedDays) / totalCycleDays : allowance;
      break;
    case "lump_sum":
    default:
      // Si el contrato empieza dentro del ciclo y el modo es lump_sum, ya prorrateamos arriba.
      accrued = allowance;
      break;
  }

  if (policy.max_balance != null) {
    const cap = Number(policy.max_balance);
    if (Number.isFinite(cap)) accrued = Math.min(accrued, cap);
  }

  return Math.max(0, accrued);
}

/**
 * Formatea el saldo acorde al display_unit y unidad de la política.
 *   - "Ilimitado" si Infinity
 *   - null si no hay política
 *   - sino, número redondeado al granular del display_unit + unidad
 */
export function formatAllowanceBalance(value, policy) {
  if (value === null || value === undefined) return "—";
  if (!Number.isFinite(value)) return "Ilimitado";

  const granularity = policy?.display_unit === "quarter"
    ? 0.25
    : policy?.display_unit === "half"
      ? 0.5
      : 1;
  // Redondeo al alza a la granularidad (a favor del trabajador), alineado con
  // el backend (VacationAllowanceCalculator). El epsilon evita inflar valores
  // ya exactos (p. ej. 22.0 no pasa a 23).
  const rounded = Math.ceil(value / granularity - 1e-9) * granularity;
  const unit = policy?.accrual_type === "hours" ? "h" : "d";
  // Mostrar decimales solo si el granular los necesita
  const text = granularity === 1
    ? String(Math.floor(rounded))
    : rounded.toFixed(granularity === 0.5 ? 1 : 2);
  return `${text}${unit}`;
}
